import { useTranslation } from "react-i18next";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { AgreementScope, Disease } from "../models/generated/graphql";
import { distinctBy } from "../utils/distinct-by";
import { useScopeFields } from "./useScopeFields";
import { useScopes } from "./useScopes";

const getDiseasesByScopes = (scopes: Array<AgreementScope>): Array<Disease> => {
	return distinctBy(
		scopes.flatMap((scope) => scope.diseases),
		(disease) => disease.Icd10Code
	);
};

export const useAvailableDiseases = (fieldController: FlexFieldController) => {
	const { disease } = useScopeFields(fieldController.parent.context);
	const { selectedScopes, allScopes } = useScopes(fieldController.parent.context);
	const { t } = useTranslation();

	if (!allScopes.length && disease) {
		return { options: [disease], isDiseaseConditionExcluded: false };
	}

	const selectedScopeDiseases = getDiseasesByScopes(selectedScopes);
	const otherDiseases = getDiseasesByScopes(allScopes).filter((disease) =>
		selectedScopeDiseases.every((selectedScopeDisease) => selectedScopeDisease.Icd10Code !== disease.Icd10Code)
	);
	const isDiseaseConditionExcluded = selectedScopes.some((scope) =>
		scope.enrollingConditionSets.some((conditionSet) => conditionSet.isDiseaseNotEnrollingCondition)
	);

	return {
		options:
			isDiseaseConditionExcluded && otherDiseases.length
				? [
						...selectedScopeDiseases.map((disease) => ({ ...disease, group: t("filtered-options") })),
						...otherDiseases.map((disease, index, source) => ({
							...disease,
							group: t("organization-available-options"),
							appendix: index === source.length - 1 ? "type-to-search-diseases" : undefined,
						})),
				  ]
				: selectedScopeDiseases,
		isDiseaseConditionExcluded,
	};
};
